import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import * as authApi from '../api/auth';
import { setAuthToken, clearAuthToken, getStoredToken } from '../api/client';

interface User {
  id: number;
  email: string;
  role: string;
  first_name: string;
  last_name: string;
  is_verified: boolean;
}

interface Patient {
  id: number;
  first_name: string;
  last_name: string;
}

interface AuthContextType {
  user: User | null;
  patients: Patient[];
  isLoading: boolean;
  login: (credentials: { email: string; password: string }) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    try {
      const data = await authApi.getMe();
      setUser(data.user);
      setPatients(data.patients);
    } catch {
      setUser(null);
      setPatients([]);
      clearAuthToken();
    }
  }, []);

  useEffect(() => {
    const token = getStoredToken();
    if (token) {
      setAuthToken(token);
      refreshUser().finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, [refreshUser]);

  const login = async (credentials: { email: string; password: string }) => {
    const loginData = await authApi.login(credentials);
    setAuthToken(loginData.access_token);
    await refreshUser();
  };

  const logout = () => {
    clearAuthToken();
    setUser(null);
    setPatients([]);
  };

  return (
    <AuthContext.Provider value={{ user, patients, isLoading, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};